import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { WorkflowAdminRow, WorkflowAdminStep } from '../models/workflow-admin'

export const WORKFLOWS_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Flujo de trabajo',
  states: 'Estados',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/** Cuántos pasos del flujo caben como badges antes del overflow "+N". */
const MAX_INLINE_STEPS = 3

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function WorkflowActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

/** Píldora discreta para el flujo predeterminado de su acción. */
export function WorkflowDefaultBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70">
      Predeterminado
    </span>
  )
}

/** Paso del flujo como badge: punto con el color del estado + nombre. */
export function WorkflowStepBadge({ step }: { step: WorkflowAdminStep }) {
  return (
    <span className="inline-flex max-w-[10rem] items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: step.state?.color ?? '#a3a3a3' }}
      />
      <span className="truncate">{step.state?.name ?? 'Estado eliminado'}</span>
    </span>
  )
}

/** Secuencia de estados con overflow "+N" (badges, no texto truncado). */
export function WorkflowStepsCell({ steps }: { steps: WorkflowAdminStep[] }) {
  if (steps.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  const inline = steps.slice(0, MAX_INLINE_STEPS)
  const overflow = steps.length - inline.length
  return (
    <span className="flex items-center gap-1">
      {inline.map((step) => (
        <WorkflowStepBadge key={`${step.order}-${step.state?._id ?? 'gone'}`} step={step} />
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70">
          +{overflow}
        </span>
      )}
    </span>
  )
}

export const workflowsAdminColumns: ColumnDef<WorkflowAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Flujo de trabajo" />
    ),
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <span className="truncate font-medium" title={row.original.name}>
          {row.original.name}
        </span>
        {row.original.default && <WorkflowDefaultBadge />}
      </span>
    )
  },
  {
    accessorKey: 'states',
    header: ({ column }) => (
      <DataTableColumnHeader
        className="min-w-[260px]"
        column={column}
        name="Estados"
        sortable={false}
      />
    ),
    // Docs viejos pueden no traer el campo: siempre con fallback.
    cell: ({ row }) => <WorkflowStepsCell steps={row.original.states ?? []} />
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <WorkflowActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
